import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import defaultUserImg from "../../../assets/images/user/img-02.jpg";
import { getPhotoUrl } from "../../../config";
import { getJobs_id2, getJobOfferApplicationsFull } from "../../../services/userservice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useValidUserId, useUserRole } from "../../../hooks";
import Pagination from "../../Jobs/JobList2/Pagination";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ListCandidateApply = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const jobOfferId = searchParams.get("jobOfferId");

  const pageFromUrl = searchParams.get("page");
  const currentPage = pageFromUrl ? Math.max(1, parseInt(pageFromUrl, 10) || 1) : 1;
  const sizeFromUrl = searchParams.get("pageSize");
  const pageSize =
    sizeFromUrl && PAGE_SIZE_OPTIONS.includes(Number(sizeFromUrl))
      ? Number(sizeFromUrl)
      : 10;

  const [jobTitle, setJobTitle] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const { isValid } = useValidUserId();
  const userRol = useUserRole();

  // Cache del título (no cambia entre páginas)
  const jobTitleCacheRef = useRef({ id: null, title: "" });

  // AbortController para cancelar la request de la página al cambiar de página
  const abortRef = useRef(null);

  const handlePageChange = (newPage) => {
    setSearchParams({ jobOfferId, page: String(newPage), pageSize: String(pageSize) });
  };

  const handlePageSizeChange = (newSize) => {
    setSearchParams({ jobOfferId, page: "1", pageSize: String(newSize) });
  };

  // Título de la oferta: solo si cambió el id
  useEffect(() => {
    if (!jobOfferId || !isValid) return;
    if (jobTitleCacheRef.current.id === jobOfferId) {
      setJobTitle(jobTitleCacheRef.current.title);
      return;
    }
    let cancelled = false;
    getJobs_id2(jobOfferId)
      .then((data) => {
        if (cancelled) return;
        const title = data?.title || "Oferta";
        jobTitleCacheRef.current = { id: jobOfferId, title };
        setJobTitle(title);
      })
      .catch(() => {
        if (!cancelled) setJobTitle("Oferta");
      });
    return () => { cancelled = true; };
  }, [jobOfferId, isValid]);

  // Página de candidatos
  useEffect(() => {
    if (!jobOfferId || !isValid) {
      setLoading(false);
      return;
    }

    // Cancelar la request anterior si seguía viva
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let cancelled = false;
    setLoading(true);

    getJobOfferApplicationsFull(jobOfferId, currentPage, pageSize)
      .then((data) => {
        if (cancelled) return;
        setCandidates(Array.isArray(data?.items) ? data.items : []);
        setTotal(typeof data?.total === "number" ? data.total : 0);
      })
      .catch((err) => {
        if (cancelled || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error(err);
        toast.error("Error al cargar postulaciones.");
        setCandidates([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [jobOfferId, isValid, currentPage, pageSize]);

  // Nota: el backend ya devuelve paginado y ordenado, así que NO hacemos slice ni sort.
  const pageCandidates = candidates;

  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);

  if (!jobOfferId) {
    return (
      <Container className="py-5">
        <p className="text-muted">
          No se especificó la oferta. <Link to="/managejobs">Volver a ofertas</Link>.
        </p>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <section className="section">
        <Container className="pt-5">
          <Row>
            <Col lg={12}>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                <h4 className="mb-0">Postulaciones: {jobTitle}</h4>
                <Link to="/managejobs">
                  <Button color="primary" outline>Volver</Button>
                </Link>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="text-muted mt-2">Cargando postulaciones...</p>
                </div>
              ) : total === 0 ? (
                <p className="text-muted">No hay postulaciones para esta oferta.</p>
              ) : (
                <>
                  <div className="list-results-bar">
                    <span className="job-grid-results-text">
                      Mostrando {start} – {end} de {total} resultados
                    </span>
                    {(userRol === "1" || userRol === 1) && (
                      <div className="job-grid-page-size-wrap">
                        <label htmlFor="pageSizeSelect">Candidatos por página:</label>
                        <Input
                          id="pageSizeSelect"
                          type="select"
                          value={pageSize}
                          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                          className="job-grid-page-size-select"
                        >
                          {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </Input>
                      </div>
                    )}
                  </div>

                  <div className="list-unstyled mb-0">
                    {pageCandidates.map((app) => (
                      <Card key={app.employee_id} className="mb-3">
                        <CardBody className="py-3">
                          <div className="d-flex align-items-start">
                            <div className="flex-shrink-0 me-3">
                              <img
                                src={getPhotoUrl(app.photo) || defaultUserImg}
                                alt=""
                                className="rounded-circle"
                                style={{ width: "48px", height: "48px", objectFit: "cover" }}
                                onError={(e) => { e.target.src = defaultUserImg; }}
                              />
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <Link
                                to="/candidatedetails"
                                state={{
                                  returnJobOfferId: jobOfferId,
                                  returnPage: currentPage,
                                  returnPageSize: pageSize,
                                }}
                                onClick={() => localStorage.setItem("employee", app.employee_id)}
                                className="fs-16 fw-medium text-primary text-decoration-none d-block mb-1"
                              >
                                {app.name} {app.lastname}
                              </Link>
                              <p className="text-muted small fs-13 mb-0">
                                <span className="d-block">Documento: {app.document}</span>
                                <span className="d-block">Contacto: {app.contact}</span>
                                <span className="d-block">Ciudad: {app.city}</span>
                                <span className="d-block">Última formación: {app.lastEducation}</span>
                              </p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>

                  <Pagination
                    totalItems={total}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default ListCandidateApply;